<!-- OTP verification form component that handles one-time password validation
Features countdown timer, resend functionality, and error handling
Supports both email and phone number based OTP verification -->

<template>
  <!-- OTP verification form -->
  <v-form class="auth-form" @submit.prevent="onSubmit">
    <!-- OTP input field -->
    <OtpInputWithValidation
      :name="'otp'"
      :label="'OTP'"
      :length="6"
      :type="'number'"
      data-testid="otp-input"
    />

    <!-- Countdown timer display -->
    <div class="mt-3 mr-3 otp-count">0 : {{ timerCountSec }} Sec</div>

    <!-- Submit button -->
    <v-btn
      type="submit"
      class="submit-button"
      elevation="2"
      block
      :loading="isLoading"
      data-testid="otp-submit-button"
    >
      {{ COMMON_CONSTANTS.BUTTON.SUBMIT }}
    </v-btn>
  </v-form>

  <!-- Resend OTP section -->
  <div class="resendotp">
    {{ COMMON_CONSTANTS.TEXT.RESEND_OTP_TEXT }}
    <v-btn
      variant="plain"
      :loading="isResendLoading"
      type="button"
      :ripple="false"
      :disabled="disableResendOtp"
      @click="resendOTP"
    >
      {{ COMMON_CONSTANTS.BUTTON.RESEND_OTP }}
    </v-btn>
  </div>
</template>

<script setup lang="ts">
// Form validation and utility imports
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import { COMMON_CONSTANTS } from "~/constants/common-constants";
import { otpSchema, type OtpModel } from "~/utils/validation/otpSchema";
import type { IResendOtpResponse, IUidPinLoginResponse } from "~/types/login";
import { API_ENDPOINT } from "~/constants/api-endpoint";

// State management
const loginType = useState("loginType");
const organizationId = useState("organizationId");

// Composables initialization
const { isLoading, timerCountSec, resetCountDown } = useCommon();
const { handleErrorResponse } = useErrors();
const { showSnackbar } = useSnackbar();
const router = useRouter();
const loginStore = useLoginStore();

// API endpoints and state variables
const errorMessage = ref<string>("");
// const otp = ref<string>(""); // To store the OTP
const isResendLoading = ref<boolean>(false);
// const isMobile = ref<boolean>(false); // To detect mobile browser

const email = computed(() => loginStore.getEmail);
const mobile_number = computed(() => loginStore.getMobileNumber);

// Form validation setup
const { handleSubmit, setFieldError } = useForm({
  validationSchema: toTypedSchema(otpSchema),
});

// disable resend otp button
const disableResendOtp = computed(() => {
  return timerCountSec.value !== 0;
});

// TODO: test this code after the deployment on mobile
// Function to detect if the user is on a mobile browser
// const detectMobileBrowser = (): boolean => {
//     const userAgent = navigator.userAgent;

//     // Simple check for mobile browsers
//     return /android|iphone|ipad|ipod|opera mini|iemobile|wpdesktop|mobile/i.test(
//         userAgent
//     );
// };

// // Function to auto-read OTP
// const autoReadOTP = async () => {
//     if (!isMobile.value) {
//         errorMessage.value =
//             "Web OTP API is only supported on mobile browsers.";
//         return;
//     }

//     if (!("OTPCredential" in window)) {
//         console.warn("Web OTP API is not supported in this browser.");
//         errorMessage.value = "Web OTP API is not supported in this browser.";
//         return;
//     }

//     try {
//         const abortController = new AbortController(); // Optional: Allows cancellation if needed
//         const otpCredential = await navigator.credentials.get({
//             otp: { transport: ["sms"] },
//             signal: abortController.signal, // Optional
//         });

//         if (otpCredential && "code" in otpCredential) {
//             otp.value = otpCredential.code;
//             console.log("Received OTP:", otpCredential.code);
//         }
//     } catch (err) {
//         errorMessage.value = (err as Error).message || "Failed to read OTP.";
//         console.error("Error reading OTP:", err);
//     }
// };

/**
 * Handles OTP form submission
 * Validates OTP and processes authentication
 * Redirects to home page on success
 */
const onSubmit = handleSubmit(async (values: OtpModel) => {
  isLoading.value = true;

  const contactInfo = email.value
    ? { email: email.value }
    : { mobile_number: mobile_number.value };

  try {
    // Prepare and send verification request
    const body = {
      ...values,
      ...contactInfo,
      login_type: loginType.value,
      organization_id: organizationId.value,
      is_public_access: loginStore.loginTypeData.is_public_access,
    };

    const response = await useApi().post<IUidPinLoginResponse>(
      API_ENDPOINT.VERIFY_OTP,
      body,
    );

    // Update user store and redirect
    useUserStore().setUser(response.data);

    // Clear OTP sent state
    loginStore.setIsOtpSent(false);
    router.push("/home");
    return response;
  } catch (error: any) {
    timerCountSec.value = 0;

    // Handle error response and show error message
    errorMessage.value = handleErrorResponse(error.response).error;
    showSnackbar(errorMessage.value, "error");

    // Handle field errors from server
    if (error.response.data.errors) {
      Object.entries(error.response.data.errors).forEach(([field, message]) => {
        setFieldError(field as keyof OtpModel, message as string);
      });
    }
  } finally {
    // Reset loading state
    isLoading.value = false;
  }
});

/**
 * Handles OTP resend request
 * Resets countdown timer on success
 * Shows success/error notifications
 */
const resendOTP = async (): Promise<void> => {
  isResendLoading.value = true;
  try {
    // Prepare and send resend request
    const body = {
      email: email.value,
      mobile_number: mobile_number.value,
      login_type: loginType.value,
      organization_id: organizationId.value,
      is_public_access: loginStore.loginTypeData.is_public_access,
    };
    // Send resend request and handle response
    const response = await useApi().post<IResendOtpResponse>(
      API_ENDPOINT.RESEND_OTP,
      body,
    );

    // Show success message and reset timer
    showSnackbar(response.message, "success");
    // Reset countdown timer
    resetCountDown();
  } catch (error: any) {
    // Handle error response and show error message
    errorMessage.value = handleErrorResponse(error.response).error;
    showSnackbar(errorMessage.value, "error");
  } finally {
    // Reset loading state
    isResendLoading.value = false;
  }
};

// Initialize countdown timer
resetCountDown();
</script>
